// src/components/LessonHistoryPanel.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';
import { format, parseISO, isValid } from 'date-fns';

const { FiX, FiClock, FiLoader } = FiIcons;

const PAGE_SIZE = 10;

const DrawerSkeleton = ({ variant = 'overlay', inlineTransition = 'enter' }) => {
  const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';
  return (
    <motion.div
      initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
            <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </motion.div>
  );
};

/** Best-effort, schema-safe tool summary */
function renderToolSuffix(cfg) {
  if (!cfg) return null;
  try {
    const c = typeof cfg === 'string' ? JSON.parse(cfg) : cfg;
    if (c?.summary) return <span className="text-gray-600"> — {String(c.summary)}</span>;
    if (c?.filename) return <span className="text-gray-600"> ({String(c.filename)})</span>;
  } catch {}
  return null;
}

/** Renders one previous note (text + inline tool summaries) */
function HistoryItem({ note, toolsForNote }) {
  const stamp = note.lesson_datetime || note.created_at;
  let when = 'Unknown date';
  try {
    const dt = typeof stamp === 'string' ? parseISO(stamp) : stamp;
    when = isValid(dt) ? format(dt, 'EEEE d MMMM') : 'Unknown date';
  } catch {}
  return (
    <div className="py-3">
      <div className="text-xs font-semibold text-gray-700">
        {when} <span className="text-gray-500">(previous lesson)</span>
      </div>

      <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
        {note.plain_text?.trim()
          || (typeof note.content === 'string' ? note.content.slice(0, 280) : '[no text]')
        }
      </div>

      {(toolsForNote?.length ?? 0) > 0 && (
        <div className="mt-2 space-y-1">
          {toolsForNote.map(t => (
            <div key={t.id} className="text-sm text-gray-800">
              <span className="font-medium">Tool:</span>{' '}
              {t.tools?.name || 'Tool'}
              {renderToolSuffix(t.tool_config)}
            </div>
          ))}
        </div>
      )}

      <div className="my-3 h-px bg-gray-200" />
    </div>
  );
}

export default function LessonHistoryPanel({
  currentNoteId,
  studentId,      // when individual lesson
  groupId,        // when group lesson
  beforeDate,     // ISO string – show notes strictly before this moment
 onClose,
  variant = 'overlay',
  inlineTransition = 'enter'
}) {
  const [firstLoading, setFirstLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState([]);
  const [toolsByNoteId, setToolsByNoteId] = useState({});
  const [page, setPage] = useState(0);
  const [noMore, setNoMore] = useState(false);

  const scrollerRef = useRef(null);
  const sentinelRef = useRef(null);
  const ioRef = useRef(null);

  const baseFilters = useMemo(() => {
    return { studentId, groupId, beforeDate, currentNoteId };
  }, [studentId, groupId, beforeDate, currentNoteId]);

  const fetchPage = useCallback(async (pageIndex) => {
    const start = pageIndex * PAGE_SIZE;
    const end = start + PAGE_SIZE - 1;

    try {
      setError(null);
      if (pageIndex === 0) setFirstLoading(true); else setLoadingMore(true);

      let notesQ = supabase
        .from('lesson_notes')
        .select('id, content, plain_text, student_id, lesson_id, lesson_datetime, created_at')
        .neq('id', baseFilters.currentNoteId)
        .order('lesson_datetime', { ascending: false, nullsFirst: false });

      if (baseFilters.beforeDate) {
        notesQ = notesQ.lt('lesson_datetime', baseFilters.beforeDate);
      }

      if (baseFilters.studentId) {
        notesQ = notesQ.eq('student_id', baseFilters.studentId);
      } else if (baseFilters.groupId) {
        const { data: lessonIds, error: lerr } = await supabase
          .from('lessons')
          .select('id')
          .eq('group_id', baseFilters.groupId)
          .order('id', { ascending: false })
          .limit(500);
        if (lerr) throw lerr;
        const ids = (lessonIds ?? []).map(r => r.id);
        if (ids.length === 0) {
          if (pageIndex === 0) setRows([]);
          setNoMore(true);
          return;
        }
        notesQ = notesQ.in('lesson_id', ids);
      } else {
        if (pageIndex === 0) setRows([]);
        setNoMore(true);
        return;
      }

      notesQ = notesQ.range(start, end);

      const { data: pageRows, error: nerr } = await notesQ;
      if (nerr) throw nerr;

      if (!pageRows || pageRows.length === 0) {
        setNoMore(true);
        return;
      }

      setRows(prev => (pageIndex === 0 ? pageRows : [...prev, ...pageRows]));

      const noteIds = pageRows.map(r => r.id);
      const { data: tools, error: terr } = await supabase
        .from('lesson_tools')
        .select(`
          id,
          lesson_note_id,
          tool_config,
          tools ( id, name, slug )
        `)
        .in('lesson_note_id', noteIds)
        .order('insert_order', { ascending: true });

      if (!terr && tools) {
        const grouped = tools.reduce((acc, t) => {
          (acc[t.lesson_note_id] ||= []).push(t);
          return acc;
        }, {});
        setToolsByNoteId(prev => ({ ...prev, ...grouped }));
      }
    } catch (err) {
      console.error('History load error', err);
      setError('Failed to load previous lessons');
    } finally {
      setFirstLoading(false);
      setLoadingMore(false);
    }
  }, [baseFilters]);

  // Initial page
  useEffect(() => {
    setRows([]);
    setToolsByNoteId({});
    setPage(0);
    setNoMore(false);
    fetchPage(0);
  }, [fetchPage]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (ioRef.current) ioRef.current.disconnect();

    ioRef.current = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !loadingMore && !firstLoading && !noMore) {
        const next = page + 1;
        setPage(next);
        fetchPage(next);
      }
    }, { root: scrollerRef.current, rootMargin: '120px' });

    ioRef.current.observe(sentinelRef.current);
    return () => ioRef.current?.disconnect();
  }, [page, firstLoading, loadingMore, noMore, fetchPage]);

  if (firstLoading) return <DrawerSkeleton variant={variant} inlineTransition={inlineTransition} />;

  const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';

  return (
    <motion.div
      initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
     exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center">
          <SafeIcon icon={FiClock} className="mr-2 text-indigo-500" />
          <h2 className="text-lg font-semibold text-gray-900">Previous Lessons</h2>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
          <SafeIcon icon={FiX} className="text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4">
        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        {rows.length === 0 && !error && (
          <div className="text-sm text-gray-600">No previous lesson notes found.</div>
        )}

        {rows.map(r => (
          <HistoryItem key={r.id} note={r} toolsForNote={toolsByNoteId[r.id]} />
        ))}

        {/* infinite-scroll sentinel */}
        <div ref={sentinelRef} />

        {/* loader */}
        {loadingMore && (
          <div className="py-4 flex items-center justify-center text-sm text-gray-600">
            <SafeIcon icon={FiLoader} className="mr-2 animate-spin" /> Loading more…
          </div>
        )}
        {noMore && rows.length > 0 && (
          <div className="py-3 text-center text-xs text-gray-500">End of history</div>
        )}
      </div>
    </motion.div>
  );
}